import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  CreateSharedStatusDto,
  OneWayInOpportunityStatusCreateDto,
  OneWayInRecommendation,
  OneWayInServiceDto,
  OneWayInServiceProviderDto,
  OneWayInStatus,
  ServiceRecommendationDtoV2,
} from 'src/api/v2';
import '../styles/Referring.scss';
import { PageParams } from '../types/PageParams';
import SendReferral from '../components/referral/SendReferral';
import ReferralSent from '../components/referral/ReferralSent';
import WriteReferral from '../components/referral/WriteReferral';
import ChooseActor from '../components/referral/ChooseActor';
import NavigationBar from '../components/referral/NavigationBar';
import useOneWayIn from '../../hooks/use-one-way-in';
import { LoadingPage } from 'src/components/LoadingPage/LoadingPage';
import { useUser } from 'src/features/authorization';
import useSiteimprove from 'src/hooks/use-siteimprove';
import { HOTJAR_DECLARATIONS, SITEIMPROVE_DECLARATIONS } from 'src/features/tracking/constants';
import useQueryParams from '../../../../hooks/use-query-params';
import useOneWayInServiceProviders from 'src/features/one-way-in/hooks/use-one-way-in-service-providers';
import OpenMenuInfoButton from 'src/features/onboarding/components/OpenMenuInfoButton/OpenMenuInfoButton';
import ServiceTable from '../components/referral/ServiceTable';
import { useCluster } from 'src/features/cluster';
import { toastPromise } from 'src/utils/toast';
import { sanitizeD365RichTextEditor } from '../../utils/htmlTextConverter';
import useHotjar from 'src/hooks/use-hotjar';

const Referring: React.FC = () => {
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const queryParams = useQueryParams();
  const { cluster } = useCluster();
  const { cooperationServiceProviders } = useOneWayInServiceProviders();

  const initialStep = queryParams.get('step') === 'write' ? 1 : 0;

  const { formId } = useParams<PageParams>();
  const [searchQuery, setSearchQuery] = useState('');
  const [step, setStep] = useState(initialStep);
  const [selectedRows, setSelectedRows] = useState<OneWayInServiceProviderDto[]>([]);
  const [selectedServices, setSelectedServices] = useState<OneWayInServiceDto[]>([]);

  const [customerEmailSelection, setCustomerEmailSelection] = useState(false);
  const [actorMessage, setActorMessage] = useState('');
  const [customerMessage, setCustomerMessage] = useState('');

  const [, setSearchParams] = useSearchParams();

  React.useEffect(() => {
    if (queryParams.get('actorIds') === null) setStep(0);
  }, [queryParams]);

  const referTo = useMemo(() => {
    const input = queryParams.get('actorIds');
    return cooperationServiceProviders.find((sp) => sp.id === input);
  }, [cooperationServiceProviders, queryParams]);

  const {
    oneWayInOpportunityById,
    oneWayInGroupedFormWOEnrollmentsQuery,
    shareOneWayInOpportunity,
    updateOneWayInOpportunityStatus,
    recommendService,
  } = useOneWayIn(formId);
  const { isSuccess, isLoading, isError } = shareOneWayInOpportunity;
  const { isAadUser, user, displayName } = useUser();
  const siteimprove = useSiteimprove();
  const hotjar = useHotjar();

  const form = oneWayInGroupedFormWOEnrollmentsQuery.data;

  const customerContactEmailAddress = useMemo(() => {
    if (!form?.kontakt?.epost) return '';
    return form?.kontakt.epost;
  }, [form]);

  const customerName = useMemo(() => {
    if (!form?.kontakt?.navn) return '';
    return form?.kontakt.navn;
  }, [form]);

  const opportunity = useMemo(() => {
    if (!oneWayInOpportunityById.data) return undefined;

    return oneWayInOpportunityById.data;
  }, [oneWayInOpportunityById.data]);

  const handleStepChange = (prev: number, next: number) => {
    if (![0, 1, 2, 3].includes(next)) return;

    setStep(next);
    if (next === 0) {
      //Actor selection table does not support initial values
      setSearchParams(`referralStatus=initiated`, { replace: true });
      setSelectedRows([]);
      return;
    }

    if (prev === 0 && next === 1) {
      setSearchParams(`referralStatus=initiated&actorIds=${selectedRows.map((row) => row.id)}`, {
        replace: true,
      });
    }
  };

  const handleSendReferral = () => {
    const createDto = {
      receivingClusterIds: [referTo!.id],
      description: actorMessage,
      sendToActor: true,
      sendToCustomer: customerEmailSelection,
      customerReceivingEmailAddresses: [customerContactEmailAddress],
      actorEmail: { message: actorMessage },
      customerEmail: { message: customerMessage },
      customerName: form?.kontakt?.navn || '',
      handlersName: displayName,
    } as CreateSharedStatusDto;

    if (selectedServices.length > 0) {
      const selectedServiceIds = selectedServices.map((service) => service.id);

      const opportunityStatusUpdate = {
        oneWayInOpportunityId: oneWayInOpportunityById.data?.id,
        referenceClusterId: undefined,
        status: OneWayInStatus.Active,
        description: actorMessage,
        createdOn: new Date(),
        closedReason: undefined,
        recommendedServices: selectedServiceIds || [],
        registeredServices: [],
        clusterContactId: undefined,
        clusterUserId: isAadUser ? undefined : user?.clusterUserId,
        handlersName: displayName || undefined,
        sendFollowUpReminderOn: undefined,
        admittedToProgram: false,
        sivaPhase: undefined,
        deliveredOn: undefined,
      } as OneWayInOpportunityStatusCreateDto;

      const serviceRecommendation = {
        recipientEmail: oneWayInGroupedFormWOEnrollmentsQuery.data?.kontakt?.epost,
        recipientName: oneWayInGroupedFormWOEnrollmentsQuery.data?.kontakt?.navn,
        services: selectedServices.map((service) => ({
          serviceName: service.name,
          links: service.externalLinks.map((link) => link.url),
          description: sanitizeD365RichTextEditor(service.description || ''),
        })),
        comment: '',
        senderName: displayName,
        senderOrganisation: cluster?.name || '',
        sendToActor: false,
        sendToCustomer: false,
      } as ServiceRecommendationDtoV2;

      const promise = recommendService.mutateAsync({
        opportunityStatusCreate: opportunityStatusUpdate,
        serviceRecommendation: serviceRecommendation,
        sharedStatusCreate: createDto,
      } as OneWayInRecommendation);

      toastPromise(promise, {
        pending: tOneWayIn('recommendToast.Pending'),
        success: tOneWayIn('recommendToast.Success'),
        error: tOneWayIn('recommendToast.Error'),
      })
        .then(() => {
          setSearchParams('referralStatus=success', { replace: true });
        })
        .catch(() => {
          siteimprove.log(
            SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.id,
            SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.actions.REFERRAL_SEND_FAILURE,
            formId,
          );
        });
    } else {
      siteimprove.log(
        SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.id,
        SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.actions.REFERRAL_SEND_ATTEMPT,
        formId,
      );
      shareOneWayInOpportunity
        .mutateAsync({
          createDto,
          opportunityId: oneWayInOpportunityById.data?.id || '',
        })
        .then(() => {
          setSearchParams('referralStatus=success', { replace: true });
          hotjar.log(
            `${HOTJAR_DECLARATIONS.UMUX.id} ${HOTJAR_DECLARATIONS.UMUX.actions.OPPORTUNITY_FORWARDED}`,
          );
          siteimprove.log(
            SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.id,
            SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.actions.REFERRAL_SEND_SUCCESS,
            formId,
          );
        })
        .catch(() => {
          siteimprove.log(
            SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.id,
            SITEIMPROVE_DECLARATIONS.FORWARDOPPORTUNITY.actions.REFERRAL_SEND_FAILURE,
            formId,
          );
        });
    }
  };

  if (isLoading || updateOneWayInOpportunityStatus.isLoading) return <LoadingPage />;

  return (
    <>
      <div className="display--flex flex-direction--row gap--2 align-items--center">
        <h2 className="margin-block--0">{tOneWayIn('referral.title')}</h2>
        <OpenMenuInfoButton page="index/commonQuestions/owi/referralProcess" />
      </div>
      {isSuccess && !updateOneWayInOpportunityStatus.isLoading ? (
        <ReferralSent />
      ) : (
        <>
          <NavigationBar
            step={step}
            handleStepChange={handleStepChange}
            handleSendReferral={handleSendReferral}
            numberOfSelectedRows={selectedRows.length}
            actorMessageEmpty={actorMessage === ''}
            customerMessageEmpty={customerMessage === ''}
            customerEmailSelection={customerEmailSelection}
            shareOpportunityError={isError}
            askIfUserWantsToArchive={!(opportunity?.isClosed ?? false)}
          />
          {step === 0 && (
            <ChooseActor
              formId={formId}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              setSelectedRows={setSelectedRows}
            />
          )}
          {step === 1 && <ServiceTable selectedActor={referTo!} setSelectedServices={setSelectedServices} />}
          {step === 2 && (
            <WriteReferral
              actorMessage={actorMessage}
              customerEmailSelection={customerEmailSelection}
              customerMessage={customerMessage}
              referTo={referTo?.name || ''}
              selectedServices={selectedServices}
              setActorMessage={setActorMessage}
              setCustomerEmailSelection={setCustomerEmailSelection}
              setCustomerMessage={setCustomerMessage}
              formTitle={form?.tittel ?? ''}
              handlersName={displayName}
              customerEmail={customerContactEmailAddress}
              customerName={customerName}
              actorName={cluster?.name || ''}
            />
          )}
          {step === 3 && (
            <SendReferral
              actorMessage={actorMessage}
              customerMessage={customerMessage}
              referTo={referTo?.name || ''}
              customerEmailSelection={customerEmailSelection}
              formTitle={form?.tittel ?? ''}
              actorName={opportunity?.status.clusterName ?? ''}
              customerName={customerName}
              handlersName={displayName}
            />
          )}
        </>
      )}
    </>
  );
};

export default Referring;
